import BootstrapSelect from 'react-bootstrap-select-dropdown';
import './style.css';
import PlusCircleIcon from '../../../../assets/img/button-icons/plus-circle.svg';
import { Fragment, useState } from 'react';
import { useEffect } from 'react';
import { translateCliniciansToOptions } from '../helpers/translateCliniciansToOptions';
import { useUpdateTeams } from '../hooks';
import { useForm } from 'react-hook-form';
import {
  closestCorners,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import DragDropContainer from './drag-drop-components/DragDropContainer';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { ClinicianItem } from './drag-drop-components';
import { isNumber } from 'lodash';
import { LoadingSpinner } from '../../LoadingSpinner';
import Select from 'react-select';
import { ClinicUnits } from './types';

export const getTeamOptions = (cliniciansByHospital, currentHospitalId) => {
  const foundedTeam = cliniciansByHospital.find(
    (item) => item.hospitalId === currentHospitalId,
  );
  if (!foundedTeam) return [];
  return translateCliniciansToOptions(foundedTeam.clinicians);
};

export const TeamListing = ({
  clinics,
  teamFilterForm,
  teams,
  isLoading,
  cliniciansByHospital,
  reloadTeams,
  setToast,
  canEditTeam,
}) => {
  const [activeDragStaff, setActiveDragStaff] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  const [openTeamCollapses, setOpenTeamCollapses] = useState(() => {
    return teams.reduce((acc, curr) => {
      acc[curr.id] = false;
      return acc;
    }, {});
  });
  const [teamStaff, setTeamStaff] = useState({});

  const [firstLoad, setFirstLoad] = useState(true);

  const addCliniciansToTeamForm = useForm({
    defaultValues: {},
  });

  const manageCliniciansBetweensTeamsForm = useForm({
    defaultValues: {},
  });

  const { postUpdateTeams, isLoading: isUpdateTeams } = useUpdateTeams();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, value },
    setValue,
  } = teamFilterForm;
  const selectedHospitalIds = watch('selected_clinics_ids');

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const clinicsOptions = Array.isArray(clinics)
    ? clinics
        .filter((clinic) => clinic.organisation_unit === ClinicUnits.HOSPITAL)
        .map((clinic) => {
          return {
            labelKey: clinic.clinic_id,
            value: clinic.clinic,
          };
        })
    : [];

  const handleClickAddCliniciansToTeam = async (teamId) => {
    setToast('Adding staff to the team..');
    const clinicianIds = addCliniciansToTeamForm.getValues(`team_${teamId}`);
    const payload = [
      {
        teamId: teamId,
        added: clinicianIds,
        removed: [],
      },
    ];
    await postUpdateTeams({
      teams: payload,
    });

    setOpenTeamCollapses((curr) => {
      return {
        ...curr,
        [teamId]: false,
      };
    });
    await reloadTeams();
    setToast('Staff added!');
  };

  const onResetListForm = () => {
    setIsDirty(false);
    manageCliniciansBetweensTeamsForm.reset();
    reloadTeams();
  };

  const handleManageTeamStaff = async () => {
    setToast('Updating team staff...');
    const formValues = manageCliniciansBetweensTeamsForm.getValues();
    const payload = Object.values(formValues).map((formValue) => {
      const toBeAdded = formValue.toBeAdded;
      const toBeRemoved = formValue.toBeRemoved;
      const teamId = Number(formValue.teamId);
      return {
        teamId: teamId,
        added: Array.isArray(toBeAdded) ? toBeAdded.map(Number) : [],
        removed: Array.isArray(toBeRemoved) ? toBeRemoved.map(Number) : [],
      };
    });
    await postUpdateTeams({
      teams: payload,
    });

    setToast('Team staff updated!');
    onResetListForm();
  };

  const handleClickRemoveCliniciansFromTeam = (staffId, teamId) => {
    setIsDirty(true);
    const toBeUpdatedValue = manageCliniciansBetweensTeamsForm.getValues(
      `team_${teamId}`,
    );
    const newValue = {
      ...toBeUpdatedValue,
      toBeRemoved:
        toBeUpdatedValue && toBeUpdatedValue.toBeRemoved
          ? [...toBeUpdatedValue.toBeRemoved, staffId]
          : [staffId],
      teamId,
    };
    manageCliniciansBetweensTeamsForm.setValue(`team_${teamId}`, newValue);
    setTeamStaff((currentTeamStaff) => {
      return {
        ...currentTeamStaff,
        [teamId]: currentTeamStaff[teamId].filter(
          (currentStaff) => currentStaff.clinicianId !== staffId,
        ),
      };
    });
  };

  const handleClickAddCliniciansFromTeam = (staffId, teamId) => {
    setIsDirty(true);
    const toBeUpdatedValue = manageCliniciansBetweensTeamsForm.getValues(
      `team_${teamId}`,
    );

    const newValue = {
      ...toBeUpdatedValue,
      toBeAdded:
        toBeUpdatedValue && toBeUpdatedValue.toBeAdded
          ? [...toBeUpdatedValue.toBeAdded, staffId]
          : [staffId],
      teamId,
    };
    manageCliniciansBetweensTeamsForm.setValue(`team_${teamId}`, newValue);
    setTeamStaff((currentTeamStaff) => {
      return {
        ...currentTeamStaff,
        [teamId]: currentTeamStaff[teamId].filter(
          (currentStaff) => currentStaff.clinicianId !== staffId,
        ),
      };
    });
  };

  function handleDragStart(event) {
    const { active } = event;
    const { id, data } = active;

    setActiveDragStaff(data.current);
  }

  function handleDragEnd(event) {
    const { active, over } = event;
    const { id } = active;
    const { id: overId } = over;
    if (id === overId) return;
    const toMoveStaffId = id.split('_')[1];
    const toRemoveTeamfId = id.split('_')[0];
    // const toAddStaffId = overId.split('_')[1];
    const toAddTeamfId = isNumber(overId) ? overId : overId.split('_')[0];

    // Find the containers
    const activeContainer = findContainer(id);
    const overContainer = findContainer(overId);

    if (
      !activeContainer ||
      !overContainer ||
      activeContainer === overContainer
    ) {
      return;
    }
    handleClickAddCliniciansFromTeam(toMoveStaffId, toAddTeamfId);
    handleClickRemoveCliniciansFromTeam(toMoveStaffId, toRemoveTeamfId);

    setTeamStaff((prev) => {
      const activeItems = prev[activeContainer];
      const overItems = prev[overContainer];

      // Find the indexes for the items
      const activeIndex = activeItems.findIndex(
        (item) => `${activeContainer}_${item.clinicianId}` === id,
      );
      const overIndex = overItems.findIndex(
        (item) => `${overContainer}_${item.clinicianId}` === overId,
      );

      let newIndex = overItems.length + 1;

      const preClinician_id = Number(id.split('_')[1]);
      return {
        ...prev,
        [activeContainer]: [
          ...prev[activeContainer].filter(
            (item) => `${activeContainer}_${item.clinicianId}` !== active.id,
          ),
        ],
        [overContainer]:
          prev[overContainer].findIndex(
            (item) => item.clinicianId === preClinician_id,
          ) === -1
            ? [
                ...prev[overContainer].slice(0, newIndex),
                teamStaff[activeContainer][activeIndex],
                ...prev[overContainer].slice(
                  newIndex,
                  prev[overContainer].length,
                ),
              ]
            : prev[overContainer],
      };
    });

    setActiveDragStaff(null);
  }

  function findContainer(id) {
    if (id in teamStaff) {
      return id;
    }

    return Object.keys(teamStaff).find((key) =>
      teamStaff[key].find((item) => `${key}_${item.clinicianId}` === id),
    );
  }

  useEffect(() => {
    if (teams && teams.length > 0) {
      setOpenTeamCollapses(() => {
        return teams.reduce((acc, curr) => {
          acc[curr.id] = false;
          return acc;
        }, {});
      });
      setTeamStaff(() => {
        return teams.reduce((acc, curr) => {
          acc[curr.id] = curr.staffs;
          return acc;
        }, {});
      });
    }
  }, [teams]);

  useEffect(() => {
    if (!isLoading && firstLoad) {
      setFirstLoad(false);
    }
  }, [isLoading]);

  const shouldDisabledButton = isLoading || isUpdateTeams;

  return (
    <>
      <div>
        <div className="team-header">
          <div className="team-header-heading">Team info</div>
          <div>
            <label className="txtDropdownLabel">See teams for:</label>
            <div className="divSelect">
              <BootstrapSelect
                style={{ width: '350px' }}
                isMultiSelect={true}
                menuSize={800}
                placeholder={'Select'}
                options={clinicsOptions}
                defaultOptions={
                  Array.isArray(clinicsOptions) && clinicsOptions.length
                    ? [clinicsOptions[0].labelKey]
                    : []
                }
                showTick={true}
                onChange={(selectedOptions) => {
                  // if (!firstLoad) setToast('Reloading team...');
                  setValue('selected_clinics_ids', selectedOptions.selectedKey);
                  onResetListForm();
                }}
              />
            </div>
          </div>
        </div>
        <div className="team-content-padding">
          <div className="text-bold mt-4 mb-2" style={{ fontSize: '15px' }}>
            Current teams
          </div>
          {isLoading && (
            <div className="my-4">
              <LoadingSpinner showText="Loading teams..." />
            </div>
          )}
          {!shouldDisabledButton && (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCorners}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <div className="team-list-container">
                {teams.map((team) => {
                  return (
                    <Fragment key={team.id}>
                      <div className="team-list-item">
                        <div className="text-bold" style={{ fontSize: '14px' }}>
                          {team.clinicName}
                        </div>
                        <DragDropContainer
                          id={team.id}
                          className="team-staff-list"
                          items={teamStaff[team.id] || []}
                          setToast={setToast}
                          onRemoveClick={(staffId, teamId) =>
                            handleClickRemoveCliniciansFromTeam(staffId, teamId)
                          }
                          canEditTeam={canEditTeam}
                          enabledDrag={
                            selectedHospitalIds.length === 1 && canEditTeam
                          }
                        />
                        <div>
                          {canEditTeam ? (
                            <button
                              className="icon-button"
                              onClick={() => {
                                setOpenTeamCollapses((curr) => {
                                  const teamId = team.id;
                                  return {
                                    ...curr,
                                    [teamId]: true,
                                  };
                                });
                              }}
                            >
                              <b>Add member</b>{' '}
                              <img
                                src={PlusCircleIcon}
                                style={{ width: '20px', marginLeft: '5px' }}
                                alt="add team member"
                              />
                            </button>
                          ) : null}
                        </div>
                        {openTeamCollapses[team.id] ? (
                          <>
                            <label
                              className="txtLeft txtDropdownLabel "
                              style={{ whiteSpace: 'normal', marginTop: 20 }}
                            >
                              Select staff member
                            </label>
                            <Select
                              style={{ width: '350px' }}
                              isMulti
                              menuSize={800}
                              options={getTeamOptions(
                                cliniciansByHospital,
                                team.parentClinicId,
                              )}
                              placeholder={'Start typing to search'}
                              showTick={true}
                              onChange={(selectedOptions) => {
                                const ids = selectedOptions.map(
                                  (item) => item.value,
                                );
                                addCliniciansToTeamForm.setValue(
                                  `team_${team.id}`,
                                  ids,
                                );
                              }}
                            />

                            <div className="team-actions">
                              <button
                                className="btn cancel-add-member-btn"
                                style={{ color: 'black', fontWeight: 600 }}
                                disabled={shouldDisabledButton}
                                onClick={() => {
                                  setOpenTeamCollapses((curr) => {
                                    const teamId = team.id;
                                    return {
                                      ...curr,
                                      [teamId]: false,
                                    };
                                  });
                                }}
                              >
                                Cancel + close
                              </button>
                              <button
                                className="btn btnPrimary btn-xlong"
                                disabled={shouldDisabledButton}
                                onClick={() =>
                                  handleClickAddCliniciansToTeam(team.id)
                                }
                              >
                                Save
                              </button>
                            </div>
                          </>
                        ) : null}
                      </div>
                    </Fragment>
                  );
                })}
              </div>
              <DragOverlay>
                {activeDragStaff ? (
                  <ClinicianItem user={activeDragStaff} />
                ) : null}
              </DragOverlay>
            </DndContext>
          )}
        </div>
      </div>
      {isDirty && canEditTeam ? (
        <div className="settings-page-actions p-4">
          <button
            type="button"
            className="cancel-change"
            onClick={onResetListForm}
            disabled={shouldDisabledButton}
          >
            Cancel changes
          </button>
          <button
            type="button"
            className="save-change"
            onClick={handleManageTeamStaff}
            disabled={shouldDisabledButton}
          >
            Save changes
          </button>
        </div>
      ) : null}
    </>
  );
};
